/**
 * Admin Node RPC definitions
 *
 * Admin endpoints for node (agent installation) management - requires admin role.
 * Nodes are joined with onboarding customer data and Valkey health status.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { AgentInstallationStatus } from "@fm/db/frontend";
import { Schema } from "effect";
import { AdminMiddleware } from "../admin.tags.ts";
import { DatabaseError, Forbidden, RecordNotFoundError, Unauthorized, ValkeyError } from "../errors.ts";

// ============================================================================
// Shared Schemas
// ============================================================================

/**
 * Node health status derived from Valkey last_seen timestamp.
 *
 * - `online`: last_seen within the last 5 minutes
 * - `offline`: last_seen exists but older than 5 minutes
 * - `unknown`: no last_seen data in Valkey
 *
 * @since 1.0.0
 */
export const NodeHealth = Schema.Literal("online", "offline", "unknown");
export type NodeHealth = Schema.Schema.Type<typeof NodeHealth>;

/**
 * Threshold in milliseconds for considering a node online (5 minutes).
 * @since 1.0.0
 */
export const ONLINE_THRESHOLD_MS = 5 * 60 * 1000;

/**
 * Customer data joined from the onboardings table.
 * @since 1.0.0
 */
export const NodeCustomer = Schema.Struct({
  onboardingId: Schema.String,
  name: Schema.String,
  email: Schema.String,
  phase: Schema.String,
});
export type NodeCustomer = Schema.Schema.Type<typeof NodeCustomer>;

// ============================================================================
// Request Schemas
// ============================================================================

/**
 * Pagination + filter input for listing nodes
 * @since 1.0.0
 */
export const NodeListInput = Schema.Struct({
  limit: Schema.optionalWith(Schema.Number, { default: () => 50 }),
  offset: Schema.optionalWith(Schema.Number, { default: () => 0 }),
  status: Schema.optional(AgentInstallationStatus),
});

/**
 * Input for finding a single node by ID
 * @since 1.0.0
 */
export const NodeFindByIdInput = Schema.Struct({
  id: Schema.String,
});

// ============================================================================
// Metrics Schemas
// ============================================================================

/**
 * A single sparkline data point for time-series display.
 * @since 1.0.0
 */
export const SparklinePoint = Schema.Struct({
  timestamp: Schema.String,
  value: Schema.Number,
});
export type SparklinePoint = Schema.Schema.Type<typeof SparklinePoint>;

/**
 * Daily token spend entry for the daily breakdown.
 * @since 1.0.0
 */
export const DailyTokenSpend = Schema.Struct({
  date: Schema.String,
  tokens: Schema.Number,
  cost: Schema.Number,
});
export type DailyTokenSpend = Schema.Schema.Type<typeof DailyTokenSpend>;

/**
 * Hourly request volume entry.
 * @since 1.0.0
 */
export const RequestVolumePoint = Schema.Struct({
  timestamp: Schema.String,
  count: Schema.Number,
});
export type RequestVolumePoint = Schema.Schema.Type<typeof RequestVolumePoint>;

/**
 * Input for fetching node usage metrics.
 * @since 1.0.0
 */
export const NodeMetricsInput = Schema.Struct({
  nodeId: Schema.String,
});

/**
 * Input for triggering the VPS deprovisioning workflow on a node.
 *
 * @since 1.0.0
 */
export const NodeDeprovisionInput = Schema.Struct({
  id: Schema.String,
});

/**
 * Response shape for the deprovision trigger endpoint. The workflow
 * runs asynchronously; the immediate response only acknowledges
 * dispatch.
 *
 * @since 1.0.0
 */
export const NodeDeprovisionResponse = Schema.Struct({
  installationId: Schema.String,
  dispatched: Schema.Boolean,
});
export type NodeDeprovisionResponse = Schema.Schema.Type<typeof NodeDeprovisionResponse>;

/**
 * Full usage metrics response for a single node.
 *
 * - tokenSpend: total + daily breakdown from LLM service aggregations
 * - cpu/memory: current value + 24h sparkline from Valkey agent metrics
 * - activeSessions: current count from Valkey
 * - requestVolume: requests/hour over last 7 days from Valkey
 *
 * @since 1.0.0
 */
export const NodeMetrics = Schema.Struct({
  nodeId: Schema.String,
  tokenSpend: Schema.Struct({
    total: Schema.Number,
    dailyBreakdown: Schema.Array(DailyTokenSpend),
  }),
  cpu: Schema.Struct({
    current: Schema.Number,
    history: Schema.Array(SparklinePoint),
  }),
  memory: Schema.Struct({
    current: Schema.Number,
    history: Schema.Array(SparklinePoint),
  }),
  activeSessions: Schema.Number,
  requestVolume: Schema.Array(RequestVolumePoint),
});
export type NodeMetrics = Schema.Schema.Type<typeof NodeMetrics>;

// ============================================================================
// Response Schemas
// ============================================================================

/**
 * Node summary for list view — excludes secrets (gatewayToken, vncPassword).
 * @since 1.0.0
 */
export const NodeSummary = Schema.Struct({
  id: Schema.String,
  name: Schema.String,
  status: AgentInstallationStatus,
  serverType: Schema.String,
  location: Schema.String,
  publicIp: Schema.String,
  tailscaleIp: Schema.String,
  tailscaleHostname: Schema.String,
  health: NodeHealth,
  lastSeenAt: Schema.NullOr(Schema.String),
  customer: Schema.NullOr(NodeCustomer),
  createdAt: Schema.String,
  updatedAt: Schema.String,
});
export type NodeSummary = Schema.Schema.Type<typeof NodeSummary>;

/**
 * Paginated response for nodes
 * @since 1.0.0
 */
export const NodeListResponse = Schema.Struct({
  items: Schema.Array(NodeSummary),
  total: Schema.Number,
});

/**
 * Node detail — same as summary plus bundle info and configuredAt.
 * @since 1.0.0
 */
export const NodeDetail = Schema.Struct({
  id: Schema.String,
  name: Schema.String,
  serverId: Schema.String,
  status: AgentInstallationStatus,
  serverType: Schema.String,
  location: Schema.String,
  publicIp: Schema.String,
  tailscaleIp: Schema.String,
  tailscaleHostname: Schema.String,
  bundleName: Schema.NullOr(Schema.String),
  bundleVersion: Schema.NullOr(Schema.String),
  configuredAt: Schema.NullOr(Schema.String),
  health: NodeHealth,
  lastSeenAt: Schema.NullOr(Schema.String),
  customer: Schema.NullOr(NodeCustomer),
  createdAt: Schema.String,
  updatedAt: Schema.String,
});
export type NodeDetail = Schema.Schema.Type<typeof NodeDetail>;

// ============================================================================
// RPC Group
// ============================================================================

/**
 * Admin Node RPC group
 *
 * Provides node list and detail endpoints with customer data + health status.
 * All endpoints require admin role via AdminMiddleware.
 *
 * @since 1.0.0
 */
export class AdminNodeRpcs extends RpcGroup.make(
  Rpc.make("admin.nodes.findMany", {
    payload: NodeListInput,
    success: NodeListResponse,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError, ValkeyError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.nodes.findById", {
    payload: NodeFindByIdInput,
    success: NodeDetail,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError, ValkeyError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.nodes.metrics", {
    payload: NodeMetricsInput,
    success: NodeMetrics,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError, ValkeyError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.nodes.deprovision", {
    payload: NodeDeprovisionInput,
    success: NodeDeprovisionResponse,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
) {}
